import { lazy, useEffect } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import LazyOnView from './components/LazyOnView';
import LoadingBar from './components/LoadingBar';
import EasterEggs from './EasterEgg';
import TurnOffLogs from './TurnOffLogs';
import { useAppData } from '@/contexts/AppDataContext';

const About = lazy(() => import('./components/About'));
const Services = lazy(() => import('./components/Services'));
const TechProducts = lazy(() => import('./components/TechProducts'));
const Portfolio = lazy(() => import('./components/Portfolio'));
const Clients = lazy(() => import('./components/Clients'));
const Team = lazy(() => import('./components/Team'));
const Contact = lazy(() => import('./components/Contact'));
const Footer = lazy(() => import('./components/Footer'));

/** Above-the-fold can paint before CMS responds; below-fold waits for real flags. */
const DEFAULT_VISIBILITY = {
  navbar: true,
  hero: true,
  about: false,
  services: false,
  techProducts: false,
  portfolio: false,
  clients: false,
  team: false,
  contact: false,
  footer: false,
};

const FALLBACK_VISIBILITY = {
  navbar: true,
  hero: true,
  about: true,
  services: true,
  techProducts: true,
  portfolio: true,
  clients: true,
  team: true,
  contact: true,
  footer: true,
};

function App() {
  const { appData, loading } = useAppData();
  const visibility =
    appData?.visibility ?? (loading ? DEFAULT_VISIBILITY : FALLBACK_VISIBILITY);

  useEffect(() => {
    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      anchor.addEventListener('click', function (e) {
        e.preventDefault();
        const target = document.querySelector(this.getAttribute('href'));
        if (target) {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      });
    });
    EasterEggs();
    TurnOffLogs();
  }, []);

  return (
    <div className="App overflow-x-hidden w-full max-w-full">
      <LoadingBar active={loading} />
      {visibility.navbar && <Navbar />}
      {visibility.hero && <Hero />}
      {visibility.about && (
        <LazyOnView minHeight="600px">
          <About />
        </LazyOnView>
      )}
      {visibility.services && (
        <LazyOnView minHeight="600px">
          <Services />
        </LazyOnView>
      )}
      {visibility.techProducts && (
        <LazyOnView minHeight="750px">
          <TechProducts />
        </LazyOnView>
      )}
      {visibility.portfolio && (
        <LazyOnView minHeight="700px">
          <Portfolio />
        </LazyOnView>
      )}
      {visibility.clients && (
        <LazyOnView minHeight="600px">
          <Clients />
        </LazyOnView>
      )}
      {visibility.team && (
        <LazyOnView minHeight="700px">
          <Team />
        </LazyOnView>
      )}
      {visibility.contact && (
        <LazyOnView minHeight="700px">
          <Contact />
        </LazyOnView>
      )}
      {visibility.footer && (
        <LazyOnView minHeight="300px">
          <Footer />
        </LazyOnView>
      )}
    </div>
  );
}

export default App;
